/**
 * Grade-level ordering helpers.
 *
 * Grade labels reach the client as free-form display strings ("PK", "Pre-K",
 * "Kindergarten", "3", "Grade 5"), so a plain lexicographic sort puts "10"
 * before "2" and buries Kindergarten in the middle of the alphabet. These
 * helpers map a label onto a numeric rank (PK < K < 1 < 2 < ... < 12) so any
 * surface that groups or lists students by grade reads the way a school does.
 *
 * The ranking logic lived in triplicate (the Houses setup route and the mobile
 * unassigned-students panel each carried a private copy); it now lives here so
 * fixes land once. {@link compareByGrade} exists for the Houses welcome-letter
 * PDF (ENG-9579), where a school prints hundreds of letters and hand-sorts the
 * stack by grade before handing each grade's pile to its teachers.
 *
 * Note: `parsers/shared.ts` still has its own `getGradeSortOrder` on a
 * different numeric scale with different consumers. Consolidating that one is
 * deliberately out of scope here.
 */

/**
 * Object shape {@link compareByGrade} can order. Typed structurally (rather
 * than against a concrete student model) so callers with their own row types
 * can use the comparator without a shared import or a cast.
 */
export interface GradeSortableStudent {
  readonly grade?: string | null;
  readonly lastName?: string | null;
  readonly firstName?: string | null;
}

/**
 * Rank a grade label for sorting: PK < K < 1 < 2 < ... < 9 < 10 < 11 < 12.
 *
 * Recognizes the bare forms ("PK", "pre-k", "pre k", "K", "kindergarten"), a
 * bare number, and the "Grade N" / "Grade K" / "Grade PK" prefixed forms.
 * Anything unrecognized (including an empty string) ranks 100 so unknown
 * grades collect at the end rather than scattering through the list.
 *
 * @param grade - The grade label as displayed, e.g. "PK", "Kindergarten", "Grade 5".
 * @returns A numeric rank: -2 for pre-K, -1 for kindergarten, N for grade N, 100 for unknown.
 *
 * @example
 * getGradeOrder('PK');       // => -2
 * getGradeOrder('Grade K');  // => -1
 * getGradeOrder('Grade 10'); // => 10
 * getGradeOrder('Ungraded'); // => 100
 */
export function getGradeOrder(grade: string): number {
  const lower = grade.toLowerCase().trim();
  if (lower === 'pk' || lower === 'pre-k' || lower === 'pre k' || lower === 'grade pk' || lower === 'grade pre-k') return -2;
  if (lower === 'k' || lower === 'kindergarten' || lower === 'grade k') return -1;
  const num = parseInt(grade, 10);
  if (!isNaN(num)) return num;
  // Handle "Grade 5", "Grade 10", etc.
  const match = lower.match(/grade\s*(\d+)/);
  if (match) return parseInt(match[1], 10);
  return 100; // Unknown grades sort last
}

/**
 * Compare two students by grade level, then last name, then first name.
 *
 * The name tiebreaks matter as much as the grade: they make each grade band
 * internally alphabetical, so a printed stack can be handed to a teacher and
 * flipped through without re-sorting. Missing names and grades are treated as
 * empty strings, which keeps unknown grades at the end (see
 * {@link getGradeOrder}) and unnamed rows at the front of their band.
 *
 * @param a - First student to compare.
 * @param b - Second student to compare.
 * @returns Negative if `a` sorts first, positive if `b` sorts first, 0 if equivalent.
 *
 * @example
 * // Sort a COPY — Array.prototype.sort mutates in place.
 * const ordered = [...students].sort(compareByGrade);
 */
export function compareByGrade(
  a: GradeSortableStudent,
  b: GradeSortableStudent,
): number {
  const gradeDelta = getGradeOrder(a.grade ?? '') - getGradeOrder(b.grade ?? '');
  if (gradeDelta !== 0) return gradeDelta;

  const lastNameDelta = (a.lastName ?? '').localeCompare(b.lastName ?? '');
  if (lastNameDelta !== 0) return lastNameDelta;

  return (a.firstName ?? '').localeCompare(b.firstName ?? '');
}
